import { supabase } from './supabase';
import { SNAPSHOT_VERSION } from '../utils/homeChanges';

// ─── "מה השתנה מאז הביקור הקודם" · the visit marker ──────────────────────────
// Two things are stored, once per user: WHEN the home screen was last opened,
// and the small snapshot the next visit is compared against.
//
// Supabase (table home_visits, migrations/sprint9.sql) is the store, with
// localStorage as a fallback for two cases: the migration has not been run
// yet, and a guest. The fallback answers "since you last opened this device",
// which is the same question one device at a time — good enough to keep the
// feature working, not good enough to be the design.
//
// ─── Why the marker is not moved on every render ────────────────────────────
// If each mount overwrote the visit, a reload two minutes later would compare
// the portfolio against itself and report nothing. The marker is therefore
// only advanced when the stored one is older than REARM_MS: within that
// window the same "since your last visit" list keeps standing, which is what
// somebody who reopens the app expects to see.

const KEY = 'si_home_visit';
const REARM_MS = 45 * 60 * 1000;

// The table may not exist yet. Remembered per session so one 404 does not
// turn into one per read.
let tableMissing = false;
const missing = err => {
  const s = `${err?.code || ''} ${err?.message || ''}`;
  return /PGRST205|42P01|does not exist|schema cache/i.test(s);
};

function localRead() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function localWrite(visit) {
  try { localStorage.setItem(KEY, JSON.stringify(visit)); } catch { /* private mode */ }
}

// { visitedAt: Date, snapshot: object } — or null for a first visit.
export async function readVisit(userId, { isGuest = false } = {}) {
  if (supabase && userId && !isGuest && !tableMissing) {
    try {
      const { data, error } = await supabase
        .from('home_visits')
        .select('visited_at, snapshot')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) {
        if (missing(error)) tableMissing = true;
        else console.warn('[homeVisit] read:', error.code, error.message);
      } else if (data) {
        return { visitedAt: new Date(data.visited_at), snapshot: data.snapshot || {} };
      } else {
        return null;                       // a real first visit for this account
      }
    } catch { /* fall through to the device store */ }
  }
  const l = localRead();
  return l ? { visitedAt: new Date(l.visitedAt), snapshot: l.snapshot || {} } : null;
}

// The visit this session compares against, read once and shared.
//
// The home screen advances the marker as soon as it has compared, so a
// second read in the same session would return "now" and every later screen
// would report that nothing changed. The company page answers the same
// question for one company · "since your previous visit" · and must use the
// SAME snapshot the home screen used. So the first read of the session is
// kept, and every reader gets it.
const baselines = new Map();
export function readBaseline(userId, { isGuest = false } = {}) {
  const k = `${userId || 'local'}|${isGuest ? 'g' : 'u'}`;
  if (!baselines.has(k)) {
    // A read that threw is not remembered · the next reader tries again rather
    // than inheriting "first visit" for the rest of the session.
    baselines.set(k, readVisit(userId, { isGuest }).catch(() => { baselines.delete(k); return null; }));
  }
  return baselines.get(k);
}

// Advances the marker, but only when the stored one has aged past REARM_MS.
// Returns true when it actually wrote.
export async function markVisit(userId, snapshot, previous, { isGuest = false } = {}) {
  if (previous && Date.now() - previous.visitedAt.getTime() < REARM_MS) return false;
  const now = new Date().toISOString();
  if (supabase && userId && !isGuest && !tableMissing) {
    try {
      const { error } = await supabase
        .from('home_visits')
        .upsert({ user_id: userId, visited_at: now, snapshot, updated_at: now });
      if (!error) { localWrite({ visitedAt: now, snapshot }); return true; }
      if (missing(error)) tableMissing = true;
      else console.warn('[homeVisit] write:', error.code, error.message);
    } catch { /* fall through */ }
  }
  localWrite({ visitedAt: now, snapshot });
  return true;
}

// What the next visit needs in order to compare: each holding's weight and
// price, and the next report date we knew about. Nothing else — this object
// is written on every visit and is capped at 8 KB by the table.
//
// Only LIVE prices go in. A holding still "pending" carries its buy price as a
// placeholder, and a snapshot taken then recorded buy prices as if they were
// quotes · the next visit reported "+87% since your last visit" for a price
// that never moved (found 2026-09-24, company page QA).
export function buildSnapshot(portfolio, reports = {}) {
  const priced = (portfolio || []).filter(p => p.quote === 'live' && p.currentPrice > 0 && p.shares > 0);
  const total = priced.reduce((a, p) => a + p.currentPrice * p.shares, 0);
  const holdings = {};
  for (const p of priced) {
    holdings[p.symbol] = {
      w: total > 0 ? Number(((p.currentPrice * p.shares) / total * 100).toFixed(2)) : 0,
      p: Number(p.currentPrice.toFixed(4)),
      // The share count, so a later visit can tell that the holding is the
      // same position (utils/homeChanges.js, checkSnapshot · 'shares').
      s: Number(p.shares),
    };
  }
  // `v: 2` marks a snapshot built by this function after every price arrived.
  // Anything without it predates the pending-price fix and is not compared
  // against (utils/homeChanges.js, checkSnapshot).
  const out = { v: SNAPSHOT_VERSION, holdings };
  const rep = {};
  for (const [sym, date] of Object.entries(reports)) if (date) rep[sym] = date;
  if (Object.keys(rep).length) out.reports = rep;
  return out;
}
